import { Link, usePage } from "@inertiajs/react";
import { usePermissions } from "./permissions";
import {
  CloseIcon,
  CustomersIcon,
  DashboardIcon,
  ExpensesIcon,
  InvoicesIcon,
  PurchasesIcon,
  ChartIcon,
  PaymentsIcon,
  LoansIcon,
  ReceiptsIcon,
  RemittancesIcon,
  ReturnsIcon,
  StatementIcon,
  ProductsIcon,
  TagIcon,
  SuppliersIcon,
  UsersIcon,
} from "./icons";

// One entry per screen, grouped the way the trade thinks rather than by what kind of record
// each one is. Everything to do with selling — who you sell to, what they were invoiced, what
// they paid — sits together, and the buying side mirrors it. Somebody chasing a payment looks
// in one place instead of remembering whether it filed under "transactions".
//
// The headings name the activity, not the records under them: "Customers → Customers" is a
// label repeating itself, and it left the section saying nothing the first item didn't.
const SECTIONS = [
  {
    items: [ { label: "Dashboard", href: "/", icon: DashboardIcon } ],
  },
  {
    title: "Administration",
    items: [ { label: "Users", href: "/users", icon: UsersIcon, superAdmin: true } ],
  },
  {
    title: "Selling",
    items: [
      { label: "Customers", href: "/customers", icon: CustomersIcon, can: "customers" },
      { label: "Invoices", href: "/invoices", icon: InvoicesIcon, can: "invoices" },
      { label: "Returns", href: "/returns", icon: ReturnsIcon, can: "returns" },
      { label: "Receipts", href: "/receipts", icon: ReceiptsIcon, can: "receipts" },
    ],
  },
  {
    title: "Buying",
    items: [
      { label: "Suppliers", href: "/suppliers", icon: SuppliersIcon, can: "suppliers" },
      { label: "Purchases", href: "/purchases", icon: PurchasesIcon, can: "purchases" },
      { label: "Payments", href: "/payments", icon: PaymentsIcon, can: "payments" },
    ],
  },
  {
    title: "Inventory",
    items: [
      { label: "Products", href: "/products", icon: ProductsIcon, can: "products" },
      { label: "Categories", href: "/categories", icon: TagIcon, can: "products" },
    ],
  },
  {
    title: "Expenses",
    items: [ { label: "Expenses", href: "/expenses", icon: ExpensesIcon, can: "expenses" } ],
  },
  {
    title: "Reports",
    items: [
      { label: "Sales Report", href: "/reports/sales", icon: ChartIcon, can: "reports_sales" },
      { label: "Product Report", href: "/reports/products", icon: ProductsIcon, can: "reports_products" },
      { label: "Profit Report", href: "/reports/profit", icon: ChartIcon, can: "reports_profit" },
      { label: "Customer Statement", href: "/reports/statement", icon: StatementIcon, can: "reports_statement" },
      { label: "Supplier Statement", href: "/reports/supplier-statement", icon: StatementIcon, can: "reports_supplier_statement" },
    ],
  },
  {
    // The three halves of a return, in the order they are read: what was charged on sales,
    // what suppliers charged us, and the two netted with the period's trading beside them.
    title: "VAT",
    items: [
      { label: "Sales VAT Report", href: "/reports/sales-vat", icon: ReceiptsIcon, can: "reports_sales_vat" },
      { label: "Purchase VAT Report", href: "/reports/purchase-vat", icon: ReceiptsIcon, can: "reports_purchase_vat" },
      { label: "VAT Report", href: "/reports/vat", icon: ReceiptsIcon, can: "reports_vat" },
    ],
  },
  // Money that leaves the business for private reasons rather than for the trade: what is sent
  // home and what is lent out. Neither settles a trading account and neither belongs among the
  // documents, so they sit together at the foot of the list under a heading that says what they
  // have in common — not expenses, not sales, and nobody's balance.
  {
    title: "Remittances & Loans",
    // Borrowers is not among them on purpose: a borrower is a name and nothing else, kept for
    // the loans to point at rather than looked at in its own right. It is reached from the loans
    // list, and added without leaving the loan being written.
    items: [
      { label: "Remittances", href: "/remittances", icon: RemittancesIcon, can: "remittances" },
      { label: "Loans", href: "/loans", icon: LoansIcon, can: "loans" },
    ],
  },
];

// Shared with the top bar's quick actions, so the two can't disagree about which
// section you are in.
export function isActive(currentUrl, href) {
  const path = currentUrl.split("?")[0];
  if (href === "/") return path === "/";
  // Users owns its whole subtree, so /users/new and /users/3/edit keep it lit.
  return path === href || path.startsWith(`${href}/`);
}

export default function Sidebar({ open, onClose }) {
  const { url } = usePage();
  const { read, superAdmin } = usePermissions();

  // A screen this account cannot open is not offered. Not greyed out — a permanently dead
  // row teaches people to stop reading the list — and not left in to bounce them off the
  // dashboard either. A section whose every item has gone takes its heading with it, so an
  // account with no reports has no empty "Reports" left behind.
  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.superAdmin) return superAdmin;
      return item.can ? read(item.can) : true;
    }),
  })).filter((section) => section.items.length > 0);

  return (
    <>
      {/* Phone/tablet backdrop — the sidebar is a permanent column from lg up. */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 animate-fade-in lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`top-topbar fixed bottom-0 left-0 z-40 flex w-64 flex-col bg-brand-900 text-brand-100 transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* The brand lives in the top bar; on phones the sidebar overlays it, so
            it keeps its own way out. */}
        <div className="flex justify-end px-3 pt-3 lg:hidden">
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-lg p-1.5 text-brand-200 transition hover:bg-white/10"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Spacing is deliberately tight: the nav is short, and generous gaps
            here just push the sections apart without helping anyone scan them.
            The section headings do the separating. */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {sections.map((section, index) => (
            <div key={section.title ?? index} className={index > 0 ? "mt-3" : ""}>
              {section.title && (
                <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-brand-400">
                  {section.title}
                </p>
              )}
              <ul className="space-y-1">
                {section.items.map(({ label, href, icon: ItemIcon }) => {
                  const active = isActive(url, href);
                  return (
                    <li key={href}>
                      <Link
                        href={href}
                        onClick={onClose}
                        aria-current={active ? "page" : undefined}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                          active
                            ? "bg-white text-brand-900 shadow-sm"
                            : "text-brand-100 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        <ItemIcon className="h-5 w-5 shrink-0" />
                        <span className="truncate">{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
